import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Copy, Check, Mail, MapPin, Phone } from "lucide-react";
import { ADDRESS, PHONE, PHONE_HREF, MAPS_URL } from "@/components/site/Contact";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Contact";
import { MaskLines, Reveal, Overline, EASE } from "@/components/site/Reveal";

const EMAIL = "info@clevantatechnologies.com";
const services = ["Generative AI & LLM apps", "Data engineering & analytics", "AI consulting & custom ML", "Chatbots & automation", "Not sure yet"];
const steps = [
  ["Send your brief", "Tell us about the problem, your data and what success looks like."],
  ["Discovery call", "We talk through goals, constraints and possible approaches."],
  ["Proposal", "You receive a scoped plan with phases, timeline and next steps."],
];

const Field = ({ label, id, children }) => (
  <label htmlFor={id} className="block">
    <span className="mb-2 block font-mono text-xs uppercase tracking-[0.18em] text-ink/70">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full rounded-2xl border border-ink/15 bg-white px-5 py-4 text-base text-ink outline-none transition-colors placeholder:text-ink/40 focus:border-violet focus:ring-2 focus:ring-violet/20";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [picked, setPicked] = useState([]);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (s) => setPicked(picked.includes(s) ? picked.filter((p) => p !== s) : [...picked, s]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email) || !form.message.trim()) {
      setError("Please add your name, a valid email and a short message.");
      return;
    }
    setError("");
    const subject = `Project enquiry — ${form.name}${form.company ? ` (${form.company})` : ""}`;
    const header = [`Name: ${form.name}`, `Email: ${form.email}`, form.company && `Company: ${form.company}`, picked.length > 0 && `Interested in: ${picked.join(", ")}`].filter(Boolean);
    const body = `${header.join("\n")}\n\n${form.message}`;
    window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <form onSubmit={submit} noValidate data-testid="contact-form" className="rounded-[32px] border border-ink/10 bg-[#FBF9F5] p-6 sm:p-10">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Your name *" id="name"><input id="name" data-testid="contact-input-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" /></Field>
        <Field label="Email *" id="email"><input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@company.com" /></Field>
      </div>
      <div className="mt-5"><Field label="Company" id="company"><input id="company" data-testid="contact-input-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company name" /></Field></div>
      <div className="mt-7">
        <span className="mb-3 block font-mono text-xs uppercase tracking-[0.18em] text-ink/70">I'm interested in</span>
        <div className="flex flex-wrap gap-2">
          {services.map((s, i) => (
            <motion.button whileTap={{ scale: 0.95 }} type="button" key={s} onClick={() => toggle(s)} data-testid={`contact-service-chip-${i + 1}`}
              className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${picked.includes(s) ? "border-ink bg-ink text-[#FBF9F5]" : "border-ink/15 bg-white text-ink hover:border-ink/40"}`}>
              {s}
            </motion.button>
          ))}
        </div>
      </div>
      <div className="mt-7"><Field label="Tell us about your project *" id="message"><textarea id="message" rows={5} data-testid="contact-input-message" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="What are you trying to solve? What data do you have?" /></Field></div>
      {error && <p data-testid="contact-form-error" className="mt-4 text-sm font-medium text-coral">{error}</p>}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-xs text-sm text-ink/70">Sending opens your email app with this message ready to go.</p>
        <button type="submit" data-testid="contact-form-submit" className="group inline-flex items-center justify-between gap-3 rounded-full bg-ink py-2 pl-7 pr-2 font-semibold text-[#FBF9F5] transition-colors hover:bg-violet">
          Send message
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-coral transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
        </button>
      </div>
    </form>
  );
};

const EmailCard = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(EMAIL); } catch (e) { return; }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="relative overflow-hidden rounded-[28px] bg-coral p-7 text-ink sm:p-9">
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-amber blur-[70px]" />
      <div className="relative">
        <Mail className="h-8 w-8" />
        <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em]">Prefer email?</p>
        <a href={`mailto:${EMAIL}`} data-testid="contact-page-email-link" className="mt-2 block break-words font-display text-2xl leading-tight sm:text-3xl">{EMAIL}</a>
        <button onClick={copy} data-testid="contact-page-copy-btn" className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-ink px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-ink hover:text-[#FBF9F5]">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? "Copied" : "Copy email"}
        </button>
      </div>
    </div>
  );
};

export default function ContactPage() {
  return (
    <>
      <Nav />
      <main id="top" data-testid="contact-page" className="relative overflow-hidden">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-violet/20 blur-[120px]" />
        <div className="pointer-events-none absolute -left-40 top-[40%] h-[420px] w-[420px] rounded-full bg-emerald/20 blur-[120px]" />
        <section className="relative mx-auto max-w-7xl px-5 pb-12 pt-32 sm:px-6 sm:pt-40 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="mb-8 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-4 py-2 font-mono text-xs uppercase tracking-[0.18em] text-ink">
            <span className="h-2 w-2 rounded-full bg-coral" /> Contact
          </motion.p>
          <h1 data-testid="contact-page-heading" className="font-display text-[3.2rem] leading-[0.95] tracking-tight text-ink sm:text-7xl lg:text-[7rem]">
            <MaskLines delay={0.25} lines={["Let's build", <em className="text-violet">something intelligent.</em>]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.8 }} className="mt-8 max-w-xl text-base leading-relaxed text-ink/75 md:text-lg">
            Share a little about your project and we'll get back to you to set up a discovery conversation.
          </motion.p>
        </section>

        <section className="relative mx-auto grid max-w-7xl grid-cols-1 gap-8 px-5 pb-24 sm:px-6 lg:grid-cols-12 lg:px-8 md:pb-32">
          <Reveal className="lg:col-span-7" delay={0.1}><ContactForm /></Reveal>
          <div className="flex flex-col gap-8 lg:col-span-5">
            <Reveal delay={0.2}><EmailCard /></Reveal>
            <Reveal delay={0.25}>
              <div data-testid="contact-office-card" className="rounded-[28px] border border-ink/10 bg-white p-7 sm:p-9">
                <a href={PHONE_HREF} data-testid="contact-page-phone-link" className="group flex items-center gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald/15 text-ink transition-colors group-hover:bg-emerald"><Phone className="h-5 w-5" /></span>
                  <span>
                    <span className="block font-mono text-xs uppercase tracking-[0.18em] text-ink/70">Call us</span>
                    <span className="font-display text-2xl text-ink sm:text-3xl">{PHONE}</span>
                  </span>
                </a>
                <div className="mt-6 flex gap-4 border-t border-ink/10 pt-6">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber/20 text-ink"><MapPin className="h-5 w-5" /></span>
                  <div>
                    <span className="block font-mono text-xs uppercase tracking-[0.18em] text-ink/70">Visit us</span>
                    <address data-testid="contact-page-address" className="mt-1 text-base not-italic leading-relaxed text-ink">
                      <strong className="font-semibold">Clevanta Technologies</strong><br />{ADDRESS}
                    </address>
                    <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-page-maps-link" className="link-underline mt-3 inline-flex items-center gap-1 text-sm font-semibold text-violet">
                      Open in Google Maps <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <div data-testid="contact-next-steps" className="rounded-[28px] bg-ink p-7 text-[#FBF9F5] sm:p-9">
                <Overline color="bg-amber"><span className="text-white/75">What happens next</span></Overline>
                {steps.map(([t, d], i) => (
                  <div key={t} className="flex gap-5 border-t border-white/15 py-5 last:pb-0">
                    <span className="font-mono text-sm text-amber">0{i + 1}</span>
                    <div>
                      <p className="font-display text-2xl">{t}</p>
                      <p className="mt-1 text-sm leading-relaxed text-white/75">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
